import type { ReportSpec } from '@clockwise/semantic-core';
import type { CSSProperties } from 'react';

/** How many `--dash-accent-*` colours `dashboard-theme.css` declares. */
const ACCENT_COUNT = 8;

/** How many `--rk-series-*` slots report-kit's chart adapter cycles through. */
const SERIES_COUNT = 6;

/**
 * A tile's colours, first to last, by its place in the definition.
 *
 * By POSITION rather than by measure: two different measures hashed onto one
 * colour would put twins side by side, and position guarantees neighbouring
 * tiles start on different colours. The price is that reordering tiles
 * recolours them — acceptable for a colour that identifies a tile, not a
 * quantity.
 *
 * The rest of the list continues round the accent palette from there, so a
 * tile of two measures gets two colours that are also this tile's own.
 */
export function tilePalette(index: number): readonly string[] {
  return Array.from(
    { length: SERIES_COUNT },
    (_, k) => `var(--dash-accent-${((index + k) % ACCENT_COUNT) + 1})`,
  );
}

/**
 * Whether this tile's chart colours carry meaning across tiles.
 *
 * With a dimension on Columns each colour IS a member of it — Retainer, Hourly
 * — and has to read the same in every tile that shows it, so the named
 * palette stays. With nothing on Columns the series are the tile's own
 * measures (or, for a metric, its one number), which identify nothing outside
 * the tile, and the tile's palette takes over.
 *
 * A PIE IS EXCLUDED: its colours are its categories, not its measure.
 */
function paintsOwnSeries(spec: Pick<ReportSpec, 'cols' | 'viz'>): boolean {
  return spec.cols.length === 0 && spec.viz !== 'table' && spec.viz !== 'pie';
}

/**
 * The inline custom properties that give a tile its own series colours, or
 * `undefined` where the shared palette must stand.
 *
 * Bound as `--rk-color-series-*`, report-kit's INPUT tokens, so they reach a
 * view mounted anywhere below this element — a tile's `.rk-scope` on the same
 * node, or a builder's `.rk-builder` further down — and beat the theme's own
 * class binding by being inline.
 */
export function tileSeriesStyle(
  spec: Pick<ReportSpec, 'cols' | 'viz'> | null | undefined,
  palette: readonly string[] | undefined,
): CSSProperties | undefined {
  if (!spec || !palette || palette.length === 0 || !paintsOwnSeries(spec)) return undefined;
  const style: Record<string, string> = {};
  palette.forEach((colour, i) => {
    style[`--rk-color-series-${i + 1}`] = colour;
  });
  return style as CSSProperties;
}
